import { Maximize2 } from "lucide-react";
import { CaseImage, PhoneFrame } from "./CaseMedia";

function GalleryLayout({ block, bi, thumbOf, onOpen }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
      {block.images.map((img, ii) => (
        <figure
          key={img.src}
          className="flex flex-col"
          data-testid={`work-${bi}-gallery-${ii}`}
        >
          <button
            type="button"
            onClick={() => onOpen(img)}
            className="group block w-full text-left"
            data-testid={`work-${bi}-thumb-${ii}`}
          >
            <div className="relative aspect-[4/3] overflow-hidden border border-[#0A0A0A] bg-white">
              <img
                src={thumbOf(img.src)}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.04]"
              />
              <span className="absolute top-2 right-2 label-mono bg-white/90 border border-[#0A0A0A] px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                View <Maximize2 size={12} strokeWidth={1.5} />
              </span>
            </div>
          </button>
          {(img.label || img.caption) && (
            <figcaption className="mt-3">
              {img.label && (
                <span className="label-mono text-[#0A0A0A] block">
                  {img.label}
                </span>
              )}
              {img.caption && (
                <span className="text-xs text-[#595959] leading-relaxed mt-1 block font-mono-ibm">
                  {img.caption}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

function PhonesLayout({ block, bi, onOpen }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 max-w-4xl">
      {block.images.map((img, ii) => (
        <figure
          key={img.src}
          className="flex flex-col"
          data-testid={`work-${bi}-phone-${ii}`}
        >
          <button
            type="button"
            onClick={() => onOpen(img)}
            className="group block w-full max-w-[280px] mx-auto sm:mx-0"
            data-testid={`work-${bi}-phone-btn-${ii}`}
          >
            <div className="relative transition-transform duration-500 group-hover:scale-[1.03]">
              <PhoneFrame src={img.src} alt={img.alt} />
              <span className="absolute top-3 right-3 z-10 label-mono bg-white/90 border border-[#0A0A0A] px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                View <Maximize2 size={11} strokeWidth={1.5} />
              </span>
            </div>
          </button>
          {(img.label || img.caption) && (
            <figcaption className="mt-3 max-w-[280px] mx-auto sm:mx-0">
              {img.label && (
                <span className="label-mono text-[#0A0A0A] block">
                  {img.label}
                </span>
              )}
              {img.caption && (
                <span className="text-xs text-[#595959] leading-relaxed mt-1 block font-mono-ibm">
                  {img.caption}
                </span>
              )}
            </figcaption>
          )}
        </figure>
      ))}
    </div>
  );
}

function SplitLayout({ block, bi, onOpen }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-center">
      <div
        className={`grid gap-4 md:gap-6 ${
          block.images.length > 1 ? "sm:grid-cols-2" : ""
        }`}
      >
        {block.images.map((img, ii) => (
          <figure key={img.src} className="flex flex-col">
            <button
              type="button"
              onClick={() => onOpen(img)}
              className="group block w-full max-w-[240px] mx-auto"
              data-testid={`work-${bi}-split-${ii}`}
              aria-label={`Open ${img.alt}`}
            >
              <div className="relative transition-transform duration-500 group-hover:scale-[1.03]">
                <PhoneFrame src={img.src} alt={img.alt} />
                <span className="absolute top-3 right-3 z-10 label-mono bg-white/90 border border-[#0A0A0A] px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  View <Maximize2 size={11} strokeWidth={1.5} />
                </span>
              </div>
            </button>
            {img.label && (
              <figcaption className="label-mono text-[#595959] mt-3 text-center">
                {img.label}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
      {block.support && (
        <div className="max-w-md">
          {block.support.label && (
            <p className="label-mono text-[#4C1D95] mb-3">
              {block.support.label}
            </p>
          )}
          <p className="text-base md:text-lg leading-relaxed text-[#0A0A0A]">
            {block.support.text}
          </p>
        </div>
      )}
    </div>
  );
}

function OpsLayout({ block, bi }) {
  return (
    <div className="space-y-12 md:space-y-16">
      <div>
        <p className="label-mono text-[#4C1D95] mb-6">Web dashboards</p>
        <div className="space-y-8 md:space-y-10">
          {block.desktop.map((img, ii) => (
            <figure key={img.src} data-testid={`work-${bi}-desktop-${ii}`}>
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto border border-[#0A0A0A] bg-white block"
              />
              {img.caption && (
                <figcaption className="text-sm text-[#595959] leading-relaxed mt-4 max-w-2xl font-mono-ibm">
                  {img.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>

      <div>
        <p className="label-mono text-[#4C1D95] mb-6">
          Guided project setup — Create a Project
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-start">
          {block.setup.map((img, ii) => (
            <figure key={img.src} data-testid={`work-${bi}-setup-${ii}`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="label-mono bg-[#4C1D95] text-white px-2 py-1">
                  {img.step}
                </span>
                <span className="label-mono text-[#0A0A0A]">{img.label}</span>
              </div>
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto border border-[#0A0A0A] bg-white block"
              />
              {img.caption && (
                <figcaption className="text-sm text-[#595959] leading-relaxed mt-4 font-mono-ibm">
                  {img.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>

      <div>
        <p className="label-mono text-[#4C1D95] mb-6">On mobile</p>
        <div className="flex flex-wrap gap-6 md:gap-8">
          {block.mobile.map((img, ii) => (
            <figure
              key={img.src}
              className="w-44 sm:w-52 flex flex-col"
              data-testid={`work-${bi}-mobile-${ii}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-auto border border-[#0A0A0A] bg-white block"
              />
              <figcaption className="label-mono text-[#0A0A0A] mt-3">
                {img.label}
              </figcaption>
              {img.caption && (
                <p className="text-xs text-[#595959] leading-relaxed mt-1 font-mono-ibm">
                  {img.caption}
                </p>
              )}
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WorkBlock({ block, bi, thumbOf, onOpen }) {
  return (
    <div
      className="max-w-[1440px] mx-auto px-4 md:px-8 py-14 md:py-20"
      data-testid={`work-block-${bi}`}
    >
      {block.layout !== "aside" && (
        <div className="max-w-3xl mb-10">
          <h3 className="font-display text-3xl md:text-4xl tracking-tighter mb-5">
            {block.heading}
          </h3>
          <p className="text-base md:text-lg leading-relaxed text-[#0A0A0A]">
            {block.text}
          </p>
        </div>
      )}

      {block.layout === "aside" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <button
              type="button"
              onClick={() => onOpen(block.images[0])}
              className="group block w-full text-left"
              data-testid={`work-${bi}-aside-btn`}
              aria-label={`Open ${block.images[0].alt}`}
            >
              <div className="relative overflow-hidden border border-[#0A0A0A] bg-white">
                <img
                  src={block.images[0].src}
                  alt={block.images[0].alt}
                  loading="lazy"
                  className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                  data-testid={`work-${bi}-image`}
                />
                <span className="absolute top-2 right-2 label-mono bg-white/90 border border-[#0A0A0A] px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  View <Maximize2 size={12} strokeWidth={1.5} />
                </span>
              </div>
            </button>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <h3 className="font-display text-3xl md:text-4xl tracking-tighter mb-5">
              {block.heading}
            </h3>
            <p className="text-base md:text-lg leading-relaxed text-[#0A0A0A] mb-5">
              {block.text}
            </p>
            {block.images[0].caption && (
              <p className="text-sm text-[#595959] leading-relaxed font-mono-ibm">
                {block.images[0].caption}
              </p>
            )}
          </div>
        </div>
      )}

      {block.layout === "full" && (
        <figure>
          <CaseImage img={block.images[0]} testid={`work-${bi}-image`} />
          {block.images[0].caption && (
            <figcaption className="text-sm text-[#595959] leading-relaxed mt-4 max-w-2xl font-mono-ibm">
              {block.images[0].caption}
            </figcaption>
          )}
        </figure>
      )}

      {block.layout === "gallery" && (
        <GalleryLayout block={block} bi={bi} thumbOf={thumbOf} onOpen={onOpen} />
      )}
      {block.layout === "phones" && (
        <PhonesLayout block={block} bi={bi} onOpen={onOpen} />
      )}
      {block.layout === "split" && (
        <SplitLayout block={block} bi={bi} onOpen={onOpen} />
      )}
      {block.layout === "ops" && <OpsLayout block={block} bi={bi} />}
    </div>
  );
}
